import React from "react";
import { Button, Spinner } from "reactstrap";
import {
	getSkippedRecommendationTitle,
	hasChangeHoverTooltip,
	mapJobChange,
	hasEligibleChangesForDeployAll,
	jobHasOpenDeployment,
	isOpenDeploymentStatus,
	OPEN_JOB_LOCK_MESSAGE,
} from "../writingAssistantUtils";
import WritingPreviewChangeStatusBanner from "./WritingPreviewChangeStatusBanner";
import WritingPreviewFixedPopover from "./WritingPreviewFixedPopover";
import { useChangeReasoningPopover } from "./useChangeReasoningPopover";

function truncateText(text, maxLen = 80) {
	if (!text || text.length <= maxLen) return text;
	return `${text.slice(0, maxLen)}...`;
}

function toStatusLabel(status) {
	return String(status || "")
		.replace(/_/g, " ")
		.replace(/\b\w/g, (m) => m.toUpperCase());
}

function getDeployStatusMeta(status, isDeploying, failureReason = "") {
	if (isDeploying) {
		return {
			label: "Deploying...",
			tone: "pending",
			allowRedeploy: false,
			openable: false,
		};
	}

	const normalized = String(status || "").toLowerCase();
	const reason = String(failureReason || "").trim();
	const isFailedStatus =
		normalized === "failed" ||
		normalized === "failed_patch" ||
		normalized === "error";

	// Per-action failure: failed* status, or a reason on a non-open change
	if (
		isFailedStatus ||
		(reason && !isOpenDeploymentStatus(normalized))
	) {
		return {
			label: "Failed",
			tone: "failed",
			allowRedeploy: true,
			openable: true,
			title: reason || undefined,
		};
	}

	switch (normalized) {
		case "":
			return null;
		case "completed":
		case "success":
		case "deployed":
		case "overwritten":
			return {
				label: "Deployed",
				tone: "completed",
				allowRedeploy: false,
				openable: true,
			};
		case "reverted":
			return {
				label: "Reverted",
				tone: "reverted",
				allowRedeploy: false,
				openable: true,
			};
		case "awaiting_review":
			return {
				label: "Awaiting review",
				tone: "pending",
				allowRedeploy: false,
				openable: true,
			};
		case "pending":
		case "queued":
		case "in_progress":
		case "processing":
		case "running":
		case "fetching":
		case "backing_up":
		case "generating_patch":
		case "applying_patch":
		case "publishing_temp":
		case "verifying":
			return {
				label: "Deploying...",
				tone: "pending",
				allowRedeploy: false,
				openable: true,
			};
		case "discarded":
			return {
				label: "Discarded",
				tone: "none",
				allowRedeploy: true,
				openable: true,
			};
		default:
			return {
				label: toStatusLabel(normalized),
				tone: "none",
				allowRedeploy: false,
				openable: true,
			};
	}
}

export default function WritingPreviewChangesSidebar({
	changes,
	skipped = [],
	debugRecommendations = [],
	activeChangeIndex,
	latestValidation,
	onSelectChange,
	autoDeployment = false,
	recommendationId = null,
	deployingIndex = null,
	isDeployingAll = false,
	onDeployChange,
	onDeployAll,
	onOpenDeployStatus,

	// Edit state propagated from the layout hook (optional)
	hasUnsavedEdits = false,
	isSavingEdits = false,
}) {
	const mappedChanges = (changes || []).map(mapJobChange);
	const skippedItems = skipped || [];
	const { popoverProps, getAnchorHandlers, getSkippedHandlers } =
		useChangeReasoningPopover();
	const hasOpenJob = jobHasOpenDeployment(mappedChanges);
	const deployDisabledByEdits = hasUnsavedEdits || isSavingEdits;
	const deployBusy = deployingIndex != null || isDeployingAll;
	const deployBlocked = deployBusy || deployDisabledByEdits || hasOpenJob;
	const deployTooltip = hasOpenJob
		? OPEN_JOB_LOCK_MESSAGE
		: hasUnsavedEdits
			? "Save your edits first, then deploy the refreshed preview."
			: isSavingEdits
				? "Saving edits – deploy will unlock in a moment."
				: "";
	const showDeployAll =
		autoDeployment &&
		recommendationId != null &&
		recommendationId !== "" &&
		hasEligibleChangesForDeployAll(mappedChanges) &&
		typeof onDeployAll === "function";

	return (
		<aside className="writing-preview__sidebar">
			<div className="writing-preview__sidebar-header">
				<h3 className="writing-preview__sidebar-title">
					AI changes ({mappedChanges.length})
				</h3>
				{showDeployAll && (
					<Button
						color="primary"
						size="sm"
						className="writing-preview__deploy-all-btn"
						disabled={deployBlocked}
						title={deployTooltip || undefined}
						onClick={(e) => {
							e.stopPropagation();
							if (deployBlocked) return;
							onDeployAll();
						}}
					>
						{isDeployingAll ? (
							<>
								<Spinner size="sm" className="me-1" />
								Deploying…
							</>
						) : (
							<>
								<i className="fas fa-cloud-upload-alt me-1" />
								Deploy all
							</>
						)}
					</Button>
				)}
			</div>
			<p className="writing-preview__sidebar-subtitle text-muted">
				Highlights follow your scroll; click to jump.
			</p>
			{showDeployAll && deployDisabledByEdits && !hasOpenJob && (
				<p className="writing-preview__deploy-blocked-note small text-muted mb-2">
					{deployTooltip}
				</p>
			)}
			{hasOpenJob && (
				<p className="writing-preview__deploy-blocked-note small text-muted mb-2">
					{OPEN_JOB_LOCK_MESSAGE}
				</p>
			)}
			<div className="writing-preview__change-list">
				{mappedChanges.length === 0 && skippedItems.length > 0 && (
					<p className="writing-preview__change-empty text-muted mb-0">
						No content changes applied.
					</p>
				)}
				{mappedChanges.map((change, index) => {
					const isActive = activeChangeIndex === index;
					const showHoverPopover = hasChangeHoverTooltip(change);
					const isDeploying = deployingIndex === index;
					const deploymentStatus = String(
						change?.deploymentStatus ||
							change?.raw?.deployment_status ||
							""
					).toLowerCase();
					const failureReason =
						change?.deploymentFailureReason ||
						change?.raw?.deployment_failure_reason ||
						"";
					const canDeployOrEdit =
						change?.allowEdit !== false &&
						change?.raw?.allow_edit !== false &&
						!isOpenDeploymentStatus(deploymentStatus);
					const deployable = change?.deployable ?? change?.raw?.deployable;
					const showDeployBtn = canDeployOrEdit && deployable === true;
					const showCantDeploy = canDeployOrEdit && deployable === false;
					const showDeployActions =
						autoDeployment && (showDeployBtn || showCantDeploy);
					const statusMeta = getDeployStatusMeta(
						change?.raw?.deployment_status || change?.deploymentStatus,
						isDeploying,
						failureReason
					);
					const showStatusChip =
						isDeploying ||
						!canDeployOrEdit ||
						Boolean(statusMeta) ||
						Boolean(String(failureReason).trim());
					return (
						<div
							key={index}
							className={`writing-preview__change-card ${
								isActive ? "writing-preview__change-card--active" : ""
							} ${
								isDeploying
									? "writing-preview__change-card--deploying"
									: ""
							}${
								showDeployActions
									? " writing-preview__change-card--has-deploy-actions"
									: ""
							}`}
						>
							<button
								type="button"
								className="writing-preview__change-card-hit"
								onClick={() => onSelectChange(index)}
							>
								<div className="writing-preview__change-card-top">
									<span
										className={`writing-preview__change-num writing-preview__change-num--${change.type}`}
									>
										{index + 1}
									</span>
									<div className="writing-preview__change-card-badges">
										<span
											className={`writing-preview__change-badge writing-preview__change-badge--${change.type}`}
										>
											{change.type === "added" ? "ADDED" : "UPDATED"}
										</span>
										<span className="writing-preview__change-tag">
											&lt;{change.tag}&gt;
										</span>
									</div>
									<div className="writing-preview__change-card-top-right">
										<WritingPreviewChangeStatusBanner
											latestValidation={latestValidation}
											changeIndex={index}
										/>
										{showStatusChip &&
											(statusMeta?.openable && onOpenDeployStatus ? (
												<button
													type="button"
													className={`writing-preview__deploy-status-chip writing-preview__deploy-status-chip--${
														statusMeta?.tone || "none"
													} writing-preview__deploy-status-chip--link`}
													title={statusMeta?.title}
													onClick={(e) => {
														e.stopPropagation();
														onOpenDeployStatus(index);
													}}
												>
													{statusMeta?.label || "Locked"}
												</button>
											) : (
												<span
													className={`writing-preview__deploy-status-chip writing-preview__deploy-status-chip--${
														statusMeta?.tone || "none"
													}`}
													title={statusMeta?.title}
												>
													{statusMeta?.label || "Locked"}
												</span>
											))}
										{isActive && (
											<i className="fas fa-magic writing-preview__change-card-sparkle" />
										)}
									</div>
								</div>
								<div
									className={`writing-preview__change-card-body ${
										showHoverPopover
											? "writing-preview__change-card--has-tooltip"
											: ""
									}`}
									{...(showHoverPopover
										? getAnchorHandlers(change, { includeWhy: true })
										: {})}
								>
									<p className="writing-preview__change-card-title mb-1">
										{truncateText(change.title, 90)}
									</p>
									<p className="writing-preview__change-card-desc mb-0">
										{truncateText(change.description, 100)}
									</p>
								</div>
							</button>
							{showDeployActions ? (
								<div className="writing-preview__change-card-actions">
									{showDeployBtn && (
										<Button
											color="primary"
											size="sm"
											className="writing-preview__deploy-btn"
											disabled={deployBlocked}
											title={deployTooltip || undefined}
											onClick={(e) => {
												e.stopPropagation();
												if (deployBlocked) return;
												onDeployChange?.(index);
											}}
										>
											{isDeploying ? (
												<>
													<Spinner size="sm" className="me-1" />
													Starting...
												</>
											) : deployBlocked ? (
												<>
													<i className="fas fa-lock me-1" />
													{statusMeta?.allowRedeploy
														? "Re-deploy"
														: "Deploy"}
												</>
											) : (
												<>
													<i className="fas fa-cloud-upload-alt me-1" />
													{statusMeta?.allowRedeploy
														? "Re-deploy"
														: "Deploy"}
												</>
											)}
										</Button>
									)}
									{showCantDeploy && (
										<span className="writing-preview__cant-deploy">
											<i className="fas fa-clock me-1" aria-hidden />
											Manual Update Required
										</span>
									)}
									{showDeployBtn && deployBlocked && deployTooltip && (
										<p className="writing-preview__deploy-blocked-note small text-muted mb-0 mt-1">
											{deployTooltip}
										</p>
									)}
									{showCantDeploy ? (
										<p className="writing-preview__deploy-blocked-note small text-muted mb-0 mt-1">
											At this moment for this change automatic deployment is
											not supported, coming soon.
										</p>
									) : null}
								</div>
							) : null}
						</div>
					);
				})}
			</div>
			{skippedItems.length > 0 && (
				<>
					<h4 className="writing-preview__skipped-title">
						Skipped ({skippedItems.length})
					</h4>
					<p className="writing-preview__sidebar-subtitle text-muted">
						These recommendations could not be applied to the page content.
					</p>
					<div className="writing-preview__change-list">
						{skippedItems.map((item, index) => {
							const showHoverPopover = hasChangeHoverTooltip(item);
							return (
								<div
									key={item.recommendation_id ?? index}
									{...getSkippedHandlers(item, debugRecommendations)}
									className="writing-preview__change-card writing-preview__change-card--skipped"
								>
									<div className="writing-preview__change-card-top">
										<span className="writing-preview__change-num writing-preview__change-num--skipped">
											{index + 1}
										</span>
										<div className="writing-preview__change-card-badges">
											<span className="writing-preview__change-badge writing-preview__change-badge--skipped">
												SKIPPED
											</span>
										</div>
									</div>
									<div
										className={`writing-preview__change-card-body ${
											showHoverPopover
												? "writing-preview__change-card--has-tooltip"
												: ""
										}`}
										{...(showHoverPopover
											? getAnchorHandlers(item, { includeWhy: true })
											: {})}
									>
										<p className="writing-preview__change-card-title mb-1">
											{truncateText(
												getSkippedRecommendationTitle(
													item,
													debugRecommendations
												),
												90
											)}
										</p>
										<p className="writing-preview__change-card-desc mb-0">
											{truncateText(item.reason, 120)}
										</p>
									</div>
								</div>
							);
						})}
					</div>
				</>
			)}
			<WritingPreviewFixedPopover {...popoverProps} />
		</aside>
	);
}
